/**
 * Utilities for image processing (cropping and compression)
 */

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
    image.src = url
  })

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation)

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

/**
 * This function returns the cropped image as a base64 string.
 */
export async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false }
) {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return null
  }

  const rotRad = getRadianAngle(rotation)

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  )

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
  ctx.translate(-image.width / 2, -image.height / 2)

  // draw rotated image
  ctx.drawImage(image, 0, 0)

  const data = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  )

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  // paste generated rotate image at the top left corner
  ctx.putImageData(data, 0, 0)

  // Return as Base64 string
  return canvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Compresses an image base64 string to be under a certain size (in bytes)
 */
export async function compressImage(base64Str, maxSizeInBytes = 500000) {
  let quality = 0.9;
  let compressedUrl = base64Str;
  
  // Helper to get size
  const getSize = (str) => atob(str.split(',')[1]).length;

  if (getSize(compressedUrl) <= maxSizeInBytes) return compressedUrl;

  const img = await createImage(base64Str);
  const canvas = document.createElement('canvas');
  let width = img.width;
  let height = img.height;

  // Initial resize if huge
  if (width > 1500 || height > 1500) {
    const ratio = width / height;
    if (width > height) {
      width = 1500;
      height = 1500 / ratio;
    } else {
      height = 1500;
      width = 1500 * ratio;
    }
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Iteratively reduce quality
  while (getSize(compressedUrl) > maxSizeInBytes && quality > 0.1) {
    compressedUrl = canvas.toDataURL('image/jpeg', quality);
    quality -= 0.1;
  }

  return compressedUrl;
}